function _extends() {
  _extends =
    Object.assign ||
    function (target) {
      for (var i = 1; i < arguments.length; i++) {
        var source = arguments[i];
        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            target[key] = source[key];
          }
        }
      }
      return target;
    };
  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly)
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    keys.push.apply(keys, symbols);
  }
  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import React, {
  createRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import styled, { css } from "styled-components";
import { v4 as uuid } from "uuid";
import { register, Registry } from "@diana-ui/registry";
import { CSSTransition } from "react-transition-group";
import * as NotificationStyles from "./NotificationStack.style";

var NotifcationStack = (_ref, ref) => {
  var { className, displayDuration = 4000, transitionTimeout = 500 } = _ref;
  var BaseNotification = Registry.get("Notification");
  var [notifications, setNotifications] = useState([]);
  var [timeouts, setTimeouts] = useState({});
  var notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  var handleRemoveNotification = useCallback(notificationId => {
    var currentNotifications = notificationsRef.current;
    var index = currentNotifications.findIndex(notification => notification.id === notificationId);

    if (index >= 0) {
      var newNotifications = [...currentNotifications];
      newNotifications.splice(index, 1);
      setNotifications(newNotifications);
    }
  }, []);
  var clearNotificationTimeout = useCallback(notificationId => {
    setTimeouts(currTimeouts => {
      var { [notificationId]: timeout } = currTimeouts,
        restTimeouts = _objectWithoutProperties(currTimeouts, [notificationId].map(_toPropertyKey));

      clearTimeout(timeout);
      return restTimeouts;
    });
  }, []);
  var setNotificationTimeout = useCallback(
    (notificationId, notification) => {
      if (notification && notification.hasTimeout !== false) {
        var duration =
          notification.displayDuration && notification.displayDuration > 0
            ? notification.displayDuration
            : displayDuration;
        var notificationTimeout = setTimeout(() => {
          handleRemoveNotification(notificationId);
          clearNotificationTimeout(notificationId);
        }, duration);
        setTimeouts(
          _objectSpread(
            _objectSpread({}, timeouts),
            {},
            {
              [notificationId]: notificationTimeout
            }
          )
        );
      }
    },
    [clearNotificationTimeout, displayDuration, handleRemoveNotification, timeouts]
  );
  var handleAddNotification = useCallback(
    notification => {
      var id = uuid();
      setNotifications([
        ...notificationsRef.current,
        _objectSpread(
          _objectSpread({}, notification),
          {},
          {
            id
          }
        )
      ]);
      setNotificationTimeout(id, notification);
      return id;
    },
    [setNotificationTimeout]
  );
  var handleMouseOver = useCallback(
    id => {
      clearNotificationTimeout(id);
    },
    [clearNotificationTimeout]
  );
  var handleMouseOut = useCallback(
    id => {
      var notification = notifications.find(not => not.id === id);
      setNotificationTimeout(id, notification);
    },
    [notifications, setNotificationTimeout]
  );
  useImperativeHandle(ref || /*#__PURE__*/ createRef(), () => ({
    addNotification: handleAddNotification,
    removeNotification: handleRemoveNotification
  }));
  return /*#__PURE__*/ React.createElement(
    NotificationStyles.StyledTransitionGroup,
    {
      className: className
    },
    notifications.map(notification => {
      var { id, template: Notification = BaseNotification } = notification;
      return /*#__PURE__*/ React.createElement(
        CSSTransition,
        {
          key: id,
          timeout: transitionTimeout
        },
        /*#__PURE__*/ React.createElement(
          Notification,
          _extends({}, notification, {
            id: id || uuid(),
            onMouseOver: notification.hasTimeout !== false ? handleMouseOver : undefined,
            onMouseOut: notification.hasTimeout !== false ? handleMouseOut : undefined
          })
        )
      );
    })
  );
};

NotifcationStack.displayName = "NotifcationStack";
register(NotifcationStack);
export default styled(/*#__PURE__*/ forwardRef(NotifcationStack)).withConfig({
  displayName: "NotificationStack"
})(_ref2 => {
  var { theme } = _ref2;
  return css(["position:fixed;z-index:1000;bottom:0;right:0;margin:", ";"], theme.spaceUnit.md);
});
